import {ChatInput, type ChatInputProps, type ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import {useNavigateToNewThread} from '@github-ui/copilot-chat/hooks/use-navigate-to-new-thread'
import {useQuotaBanner} from '@github-ui/copilot-chat/hooks/use-quota-banner'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import {useSSOOrgNames} from '@github-ui/copilot-chat/hooks/use-sso-org-names'
import {useChatStateLens} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {isFileReference, referencesAreEqual} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {
  type CopilotChatReference,
  CopilotLicenseType,
  type ModeSupport,
  ModeSupportName,
} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {AnchorSide} from '@primer/behaviors'
import {CommentIcon} from '@primer/octicons-react'
import {useCallback, useDeferredValue, useMemo, useState} from 'react'

import type {HandleMessageSubmit} from '../hooks/use-handle-message-submit'
import {useOnReferenceSelect} from '../hooks/use-on-reference-select'
import {getItemVersionForReference} from './ContentPreview/content-preview-types'
import {useContentPreview} from './ContentPreview/ContentPreviewContext'
import styles from './Layout.module.css'
import {NotificationsSection} from './NotificationsSection'

interface ChatInputContainerProps {
  activeModeName?: string
  onClickMode?: (modeName: string) => void
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  chatInputRef?: React.RefObject<ChatInputRef | null>
  handleUserSubmit: HandleMessageSubmit
  nextMessageIndex: number
  containerRef?: React.RefObject<HTMLFormElement | null>
  isNewConversation?: boolean
  onChatCommandChange?: ChatInputProps['onChatCommandChange']
  slashCommandsEnabled?: ChatInputProps['slashCommandsEnabled']
  showDisabledInputOnQuotaExceeded?: boolean
  attachmentMenuSide?: AnchorSide
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
}

export function ChatInputContainer({
  activeModeName,
  onClickMode,
  textAreaRef,
  handleUserSubmit,
  nextMessageIndex,
  chatInputRef,
  isNewConversation,
  onChatCommandChange,
  containerRef,
  slashCommandsEnabled,
  showDisabledInputOnQuotaExceeded = false,
  attachmentMenuSide,
  forceIconOnly,
}: ChatInputContainerProps) {
  // Subscribe to a boolean rather than the streamingMessage object: the object changes on every
  // streamed token, but ChatInput only needs to know whether streaming is active, so this avoids
  // re-rendering the container (and ChatInput) on each token.
  const isStreaming = useChatStateLens(s => !!s.streamingMessage)
  const {chatQuotaExceeded, isLicensedLimited, licenseType, tokenBasedBillingEnabled} = useEntitlement()

  // Check if the user has missing SSO organizations
  const {needsSsoAuthentication} = useSSOOrgNames()
  const selectedCopilotSpaceId = useSelectedCopilotSpaceId()
  const hideChatInput = needsSsoAuthentication && selectedCopilotSpaceId

  const navigateToNewThread = useNavigateToNewThread()
  // ChatInput is uncontrolled and reports its text via onTextChange. Only NotificationsSection reads
  // it (for the SSO-org banner). Because ChatInput is memoized with stable props, updating this state
  // re-renders only this container and NotificationsSection, not ChatInput. The value handed to
  // NotificationsSection is deferred so the SSO match never blocks a keystroke.
  const [inputText, setInputText] = useState('')
  const deferredInputText = useDeferredValue(inputText)

  const handleInputReferenceSelect = useOnReferenceSelect({messageIndex: nextMessageIndex})

  const {versionedItems, items, promoteTab} = useContentPreview()

  const existingFileNames = useMemo(() => {
    const result = new Set<string>()
    for (const [, item] of items) if (item.type === 'file') result.add(item.name)
    return result
  }, [items])

  const handlePromoteImplicitReference = useCallback(
    (reference: CopilotChatReference) => {
      if (!isFileReference(reference)) return
      for (const [id, item] of items) {
        if (item.type === 'file' && isFileReference(item.reference) && referencesAreEqual(item.reference, reference)) {
          promoteTab(id)
          sendEvent('dotcom_chat.activate', {
            target: 'IMPLICIT_REFERENCE_PROMOTED',
            mode: 'immersive',
            source: 'reference_pill_click',
          })
          break
        }
      }
    },
    [items, promoteTab],
  )

  const quotaBanner = useQuotaBanner()
  const quotaExceeded = tokenBasedBillingEnabled
    ? quotaBanner.show && quotaBanner.isBlocking
    : isLicensedLimited && chatQuotaExceeded
  const canUseChat = !quotaExceeded && !hideChatInput
  const shouldShowInput = canUseChat || (quotaExceeded && showDisabledInputOnQuotaExceeded)

  // Stabilize props passed to the (memoized) ChatInput so that re-renders of this container for
  // unrelated reasons (entitlement/quota/content-preview changes) don't re-render ChatInput.
  const modes = useMemo<[ModeSupport, ...ModeSupport[]]>(
    () => [
      {
        name: ModeSupportName.Ask,
        supportsAttachments: true,
        supportsModelSwitching: licenseType !== CopilotLicenseType.Unlicensed,
        supportsSpaces: true,
        supportsAutocompletion: true,
        icon: CommentIcon,
        placeholder: 'Ask anything or type @ to add context',
      },
    ],
    [licenseType],
  )

  const getReferenceVersion = useCallback(
    (reference: CopilotChatReference) => getItemVersionForReference(reference, nextMessageIndex, versionedItems, items),
    [nextMessageIndex, versionedItems, items],
  )

  const handleSubmit = useCallback<NonNullable<ChatInputProps['onSubmit']>>(
    (content, additionalReferences, onAccepted) =>
      handleUserSubmit(content, additionalReferences, undefined, undefined, undefined, undefined, onAccepted),
    [handleUserSubmit],
  )

  return (
    <div className={styles.chatInputContainer}>
      <NotificationsSection inputText={deferredInputText} />
      {shouldShowInput && (
        <ChatInput
          activeModeName={activeModeName}
          onClickMode={onClickMode}
          modes={modes}
          onNewThreadSelected={navigateToNewThread}
          textAreaRef={textAreaRef}
          chatInputRef={chatInputRef}
          containerRef={containerRef}
          onSubmit={handleSubmit}
          isStreaming={isStreaming}
          size="large"
          onSelectReference={handleInputReferenceSelect}
          onPromoteImplicitReference={handlePromoteImplicitReference}
          getReferenceVersion={getReferenceVersion}
          existingFileNames={existingFileNames}
          isNewConversation={isNewConversation}
          onChatCommandChange={onChatCommandChange}
          slashCommandsEnabled={slashCommandsEnabled}
          disabled={quotaExceeded}
          onTextChange={setInputText}
          attachmentMenuSide={attachmentMenuSide}
          forceIconOnly={forceIconOnly}
        />
      )}
    </div>
  )
}
